import React, { useState, useEffect, useMemo } from 'react';
import { IoMdEye } from 'react-icons/io';
import { MdDelete } from "react-icons/md";
import { IoSearch, IoClose } from "react-icons/io5";
import { Button, Pagination } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useMyContext } from '../../../context/MyContext';
import Sidebar from '../../../component/b2b/sideBar/b2bSideBar';
import NavBar from '../../../component/b2b/navBar/b2bNavBar';
import PaginationItem from '@mui/material/PaginationItem';
import '../../../css/custom-scrollbar.css';
import { LuPlus, LuWarehouse } from "react-icons/lu";
import axios from 'axios';
import Swal from 'sweetalert2';
import { Base_Url, deletewarehouse, b2bWarehouseList } from '../../../config/config';
import B2bViewWarehouse from '../../../component/b2b/models/viewWarehouseModel';
import AddWarehouse from './b2baddWarehouse';

const AllWarehouses = () => {
  const [totalOrders, setTotalOrders] = useState(0);
  const { isDarkMode } = useMyContext();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const { isToggleSidebar } = useMyContext();
  const [warehouses, setWarehouses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedWarehouse, setSelectedWarehouse] = useState(null);
  const [loadingWarehouseId, setloadingWarehouseId] = useState(null);
  const itemsPerPage = 8;
  const navigate = useNavigate();

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  // ⬇️ NEW: Search state
  const [searchTerm, setSearchTerm] = useState('');

  // ⬇️ Add Warehouse drawer/popup control
  const [isAddWarehouseOpen, setIsAddWarehouseOpen] = useState(false);

  useEffect(() => {
    fetchWarehouses();
  }, [page, limit]);

  const fetchWarehouses = async () => {
    const token = localStorage.getItem('token');
    const cusid = localStorage.getItem('cus_id');
    try {
      const pageNumber = Number(page) || 1;
      const limitNumber = Number(limit) || 10;
      const urlWarehouse = `${Base_Url}${b2bWarehouseList}?page=${pageNumber}&limit=${limitNumber}`
      const response = await fetch(urlWarehouse, {
        method: 'GET',
        headers: {
          'token': token,
          'cus_id': cusid,
          'Content-Type': 'application/json'
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();
      if (result.status === "SUCCESS" && Array.isArray(result.data)) {
        setWarehouses(result.data);
        setTotalOrders(result.pagination.totalItems || 0);
      } else {
        console.error('Unexpected response format:', result);
      }
    } catch (error) {
      console.error('Error fetching warehouse data:', error);
    }
  };

  const handlePaginationChange = (event, value) => {
    setPage(value);
  };

  const handleLimitChange = (event) => {
    setLimit(event.target.value);
    setPage(1);
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = warehouses.slice(indexOfFirstItem, indexOfLastItem);

  const handleViewClick = (warehouse) => {
    setSelectedWarehouse(warehouse);
    setIsPopupOpen(true);
  };

  const handleClose = () => {
    setIsPopupOpen(false);
  };

  // Function to update warehouse in the list
  const handleUpdateWarehouse = (updatedWarehouse) => {
    setWarehouses(prevWarehouses =>
      prevWarehouses.map(warehouse =>
        warehouse.id === updatedWarehouse.id ? updatedWarehouse : warehouse
      )
    );
  };

  const handleDelete = async (id) => {
    setloadingWarehouseId(id);

    Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this deletion!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#000000',
      cancelButtonColor: '#666666',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        const urldelete = Base_Url + deletewarehouse;
        try {
          const token = localStorage.getItem('token');
          const cusid = localStorage.getItem('cus_id');
          const data = { warehouseId: id };

          const response = await axios.post(urldelete, data, {
            headers: {
              token,
              cus_id: cusid,
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
          });

          if (response.data.status === "SUCCESS") {
            setWarehouses((prevWarehouses) =>
              prevWarehouses.filter((warehouse) => warehouse.id !== id)
            );
            setTotalOrders((prev) => prev - 1);

            Swal.fire(
              'Deleted!',
              response.data.message || 'Warehouse has been deleted successfully.',
              'success'
            );
          } else {
            Swal.fire(
              'Error!',
              'Unexpected response format: ' + response.data.message,
              'error'
            );
          }
        } catch (error) {
          Swal.fire(
            'Error!',
            'Error deleting warehouse: ' + error.message,
            'error'
          );
        } finally {
          setloadingWarehouseId(null);
        }
      } else {
        setloadingWarehouseId(null);
      }
    });
  };

  // ⬇️ NEW: Filtered warehouses based on search term
  const filteredWarehouses = useMemo(() => {
    if (!searchTerm.trim()) return warehouses;

    const term = searchTerm.trim().toLowerCase();

    return warehouses.filter((warehouse) => {
      return (
        String(warehouse.warehouse_id || '').toLowerCase().includes(term) ||
        String(warehouse.name || '').toLowerCase().includes(term) ||
        String(warehouse.contact_person || '').toLowerCase().includes(term) ||
        String(warehouse.phone_number || '').toLowerCase().includes(term) ||
        String(warehouse.city || '').toLowerCase().includes(term) ||
        String(warehouse.state || '').toLowerCase().includes(term) ||
        String(warehouse.country || '').toLowerCase().includes(term)
      );
    });
  }, [warehouses, searchTerm]);

  const glass =
    "backdrop-blur-2xl backdrop-saturate-150 bg-white/50 dark:bg-white/[0.06] " +
    "border border-white/60 dark:border-white/10 " +
    "shadow-[0_8px_32px_rgba(31,41,55,0.08)]";

  return (
    <>
      <div className={`h-screen flex flex-col overflow-hidden ${isDarkMode ? 'bg-black' : 'bg-white'}`}>
        <div className="pointer-events-none absolute -top-32 -left-24 h-96 w-96 rounded-full bg-indigo-200/30 dark:bg-indigo-500/10 blur-3xl" />

        <NavBar />

        <div className="flex flex-1 overflow-hidden">
                  <div
            className={`${isToggleSidebar ? "md:w-64" : "md:w-20"} w-0 m-0 md:m-3 md:rounded-3xl ${glass} overflow-visible md:overflow-hidden transition-all duration-300 ease-in-out flex-shrink-0`}
            // onClick={handlePageClick}
          >
            <Sidebar />
          </div>

          <div className={`flex flex-1 flex-col min-h-0 overflow-hidden w-full p-3 ${isDarkMode ? 'bg-black' : 'bg-gray-100'}`}>
            {/* Header Button */}
            <div className="flex justify-between items-center mb-2 gap-2 flex-wrap">
              <button
                className={`text-[9px] font-semibold mb-2 flex-shrink-0 px-3 py-1.5 rounded flex items-center justify-center transition-colors w-fit
                ${isDarkMode
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-500 hover:to-purple-500 shadow-lg shadow-indigo-900/50'
                    : 'bg-blue-800 text-white hover:bg-blue-700'
                  }`}>
                <LuWarehouse className="mr-1.5" size={13} />
                All Warehouses
              </button>

              {/* ⬇️ NEW: Search box */}
              <div className="flex-1 max-w-2xl mb-2 relative">
                <IoSearch
                  size={14}
                  className={`absolute left-2.5 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-indigo-300' : 'text-gray-400'}`}
                />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by name, city, phone..."
                  className={`w-full pl-8 pr-7 py-1.5 text-[11px] rounded-md border focus:outline-none focus:ring-1
                    ${isDarkMode
                      ? 'bg-gray-950 border-indigo-500/40 text-white placeholder-gray-500 focus:ring-indigo-500/60 focus:border-indigo-500'
                      : 'bg-white border-gray-300 text-black placeholder-gray-400 focus:ring-black/30'
                    }`}
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 ${isDarkMode ? 'text-indigo-300 hover:text-white' : 'text-gray-400 hover:text-black'}`}
                  >
                    <IoClose size={14} />
                  </button>
                )}
              </div>

              <button
                onClick={() => setIsAddWarehouseOpen(true)}
                className={`text-[9px] font-semibold mb-2 flex-shrink-0 px-3 py-1.5 rounded flex items-center justify-center transition-colors w-fit
                ${isDarkMode
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-500 hover:to-purple-500 shadow-lg shadow-indigo-900/50'
                    : 'bg-blue-800 text-white hover:bg-blue-700'
                  }`}>
                <LuPlus className="mr-1.5" size={13} />
                Create Warehouse
              </button>
            </div>

            {/* ============ DESKTOP TABLE (untouched, hidden on mobile) ============ */}
            <div
              className="hidden md:block flex-1 min-h-0 overflow-hidden border rounded-lg"
              style={{
                borderColor: isDarkMode ? 'rgba(99,102,241,0.25)' : 'rgba(0,0,0,0.1)',
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'transparent'
              }}
            >
              <div className="h-full overflow-y-auto custom-scrollbar overflow-x-auto">
                <table className="min-w-full table-auto">
                  <thead
                    className={`sticky top-0 text-[9px] uppercase tracking-wider z-10 text-white border-b
                      ${isDarkMode
                        ? 'bg-gradient-to-r from-indigo-700 via-purple-700 to-indigo-700 border-indigo-500/40'
                        : 'bg-red-600 border-white/20'
                      }`}
                  >
                    <tr>
                      <th className='px-2 py-2 text-left font-semibold'>ID</th>
                      <th className='px-2 py-2 text-left font-semibold'>Name</th>
                      <th className='px-2 py-2 text-left font-semibold'>Contact</th>
                      <th className='px-2 py-2 text-left font-semibold'>Phone</th>
                      <th className='px-2 py-2 text-left font-semibold'>City</th>
                      <th className='px-2 py-2 text-left font-semibold'>State</th>
                      <th className='px-2 py-2 text-left font-semibold'>Country</th>
                      <th className='px-2 py-2 text-center font-semibold'>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDarkMode ? 'divide-indigo-500/15' : 'divide-black/10'}`}>
                    {filteredWarehouses.length > 0 ? (
                      filteredWarehouses.map((warehouse, index) => (
                        <tr
                          key={warehouse.id}
                          className={`transition-colors duration-150 ${isDarkMode
                            ? 'bg-transparent hover:bg-indigo-500/10 text-gray-100'
                            : 'bg-white hover:bg-gray-50 text-black'
                            }`}
                        >
                          <td className='px-2 py-2 text-[11px] font-mono'>
                            <span className={isDarkMode ? 'text-indigo-300' : ''}>
                              #{warehouse.warehouse_id}
                            </span>
                          </td>
                          <td className='px-2 py-2 text-[11px] font-medium'>{warehouse.name}</td>
                          <td className='px-2 py-2 text-[11px]'>{warehouse.contact_person}</td>
                          <td className='px-2 py-2 text-[11px] font-mono'>{warehouse.phone_number}</td>
                          <td className='px-2 py-2 text-[11px]'>{warehouse.city}</td>
                          <td className='px-2 py-2 text-[11px]'>{warehouse.state}</td>
                          <td className='px-2 py-2 text-[11px]'>{warehouse.country}</td>
                          <td className='px-2 py-2'>
                            <div className='flex items-center justify-center gap-1.5'>
                              <button
                                onClick={() => handleViewClick(warehouse)}
                                className={`p-1 rounded transition-colors border ${isDarkMode
                                  ? 'border-indigo-500/40 hover:bg-indigo-500/20 hover:border-indigo-400'
                                  : 'border-black/20 hover:bg-black/10'
                                  }`}
                              >
                                <IoMdEye size={14} className={isDarkMode ? 'text-indigo-300' : 'text-blue-600'} />
                              </button>

                              <button
                                onClick={() => handleDelete(warehouse.id)}
                                disabled={loadingWarehouseId === warehouse.id}
                                className={`p-1 rounded transition-colors border ${isDarkMode
                                  ? 'border-red-500/40 hover:bg-red-500/20 hover:border-red-400'
                                  : 'border-black/20 hover:bg-black/10'
                                  } ${loadingWarehouseId === warehouse.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                {loadingWarehouseId === warehouse.id ? (
                                  <svg className="animate-spin h-3.5 w-3.5 text-red-400" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                  </svg>
                                ) : (
                                  <MdDelete size={14} className={isDarkMode ? 'text-red-400' : 'text-red-600'} />
                                )}
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className={`text-center py-6 text-[11px] ${isDarkMode ? 'text-gray-400' : 'text-black/50'}`}>
                          {searchTerm ? `No warehouses match "${searchTerm}"` : 'No warehouses found'}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ============ MOBILE CARDS (new, shown only below md) ============ */}
            <div
              className="md:hidden flex-1 min-h-0 overflow-y-auto custom-scrollbar border rounded-lg p-2 space-y-2"
              style={{
                borderColor: isDarkMode ? 'rgba(99,102,241,0.25)' : 'rgba(0,0,0,0.1)',
                backgroundColor: isDarkMode ? 'rgba(255,255,255,0.02)' : 'transparent'
              }}
            >
              {filteredWarehouses.length > 0 ? (
                filteredWarehouses.map((warehouse) => (
                  <div
                    key={warehouse.id}
                    className={`rounded-lg border p-3 ${isDarkMode
                      ? 'bg-gray-950/60 border-indigo-500/25 text-gray-100'
                      : 'bg-white border-gray-200 text-black'
                      }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className={`text-[10px] font-mono ${isDarkMode ? 'text-indigo-300' : 'text-gray-500'}`}>
                          #{warehouse.warehouse_id}
                        </p>
                        <p className="text-[13px] font-semibold mt-0.5">{warehouse.name}</p>
                      </div>

                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => handleViewClick(warehouse)}
                          className={`p-1.5 rounded transition-colors border ${isDarkMode
                            ? 'border-indigo-500/40 hover:bg-indigo-500/20 hover:border-indigo-400'
                            : 'border-black/20 hover:bg-black/10'
                            }`}
                        >
                          <IoMdEye size={15} className={isDarkMode ? 'text-indigo-300' : 'text-blue-600'} />
                        </button>

                        <button
                          onClick={() => handleDelete(warehouse.id)}
                          disabled={loadingWarehouseId === warehouse.id}
                          className={`p-1.5 rounded transition-colors border ${isDarkMode
                            ? 'border-red-500/40 hover:bg-red-500/20 hover:border-red-400'
                            : 'border-black/20 hover:bg-black/10'
                            } ${loadingWarehouseId === warehouse.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {loadingWarehouseId === warehouse.id ? (
                            <svg className="animate-spin h-3.5 w-3.5 text-red-400" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                          ) : (
                            <MdDelete size={15} className={isDarkMode ? 'text-red-400' : 'text-red-600'} />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className={`mt-2 pt-2 border-t grid grid-cols-2 gap-y-1.5 gap-x-2 text-[11px] ${isDarkMode ? 'border-indigo-500/15' : 'border-black/10'}`}>
                      <div>
                        <span className={isDarkMode ? 'text-gray-400' : 'text-black/50'}>Contact: </span>
                        <span>{warehouse.contact_person}</span>
                      </div>
                      <div>
                        <span className={isDarkMode ? 'text-gray-400' : 'text-black/50'}>Phone: </span>
                        <span className="font-mono">{warehouse.phone_number}</span>
                      </div>
                      <div>
                        <span className={isDarkMode ? 'text-gray-400' : 'text-black/50'}>City: </span>
                        <span>{warehouse.city}</span>
                      </div>
                      <div>
                        <span className={isDarkMode ? 'text-gray-400' : 'text-black/50'}>State: </span>
                        <span>{warehouse.state}</span>
                      </div>
                      <div className="col-span-2">
                        <span className={isDarkMode ? 'text-gray-400' : 'text-black/50'}>Country: </span>
                        <span>{warehouse.country}</span>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className={`text-center py-6 text-[11px] ${isDarkMode ? 'text-gray-400' : 'text-black/50'}`}>
                  {searchTerm ? `No warehouses match "${searchTerm}"` : 'No warehouses found'}
                </div>
              )}
            </div>

            {/* Footer - Pagination & Controls */}
            <div className="mt-2 flex-shrink-0 flex flex-col sm:flex-row justify-between items-center gap-2">
              <span className={`text-[9px] font-medium ${isDarkMode ? 'text-gray-300' : 'text-black/70'}`}>
                {searchTerm ? (
                  <>Showing <span className={`font-bold ${isDarkMode ? 'text-indigo-300' : ''}`}>{filteredWarehouses.length}</span> of <span className={`font-bold ${isDarkMode ? 'text-indigo-300' : ''}`}>{totalOrders}</span></>
                ) : (
                  <>Total: <span className={`font-bold ${isDarkMode ? 'text-indigo-300' : ''}`}>{totalOrders}</span></>
                )}
              </span>

              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5">
                  <span className={`text-[8px] ${isDarkMode ? 'text-gray-400' : 'text-black/50'}`}>Show</span>
                  <select
                    id="limit"
                    value={limit}
                    onChange={handleLimitChange}
                    className={`border rounded px-1.5 py-0.5 text-[9px] focus:outline-none focus:ring-1 ${isDarkMode
                      ? 'bg-gray-950 text-white border-indigo-500/40 focus:ring-indigo-500/60'
                      : 'bg-white text-black border-black/30 focus:ring-black/50'
                      }`}
                  >
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={totalOrders}>All</option>
                  </select>
                </div>

                <Pagination
                  count={Math.ceil(totalOrders / limit)}
                  page={page}
                  onChange={handlePaginationChange}
                  variant="outlined"
                  shape="rounded"
                  className="pagination"
                  size="small"
                  renderItem={(item) => (
                    <PaginationItem
                      {...item}
                      className="mx-0.5 rounded"
                      style={{
                        color: isDarkMode ? '#c7d2fe' : '#000000',
                        borderColor: isDarkMode ? 'rgba(99,102,241,0.4)' : 'rgba(0,0,0,0.3)',
                        backgroundColor: item.selected
                          ? (isDarkMode ? 'rgb(79,70,229)' : '#000000')
                          : 'transparent',
                        ...(item.selected ? { color: isDarkMode ? '#ffffff' : '#ffffff' } : {}),
                      }}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </div>
        <B2bViewWarehouse
          isopen={isPopupOpen}
          onClose={handleClose}
          warehouse={selectedWarehouse}
          onUpdate={handleUpdateWarehouse}
        />

        {/* Add Warehouse left-side popup */}
        <AddWarehouse
          isOpen={isAddWarehouseOpen}
          onClose={() => setIsAddWarehouseOpen(false)}
          onCreated={fetchWarehouses}
        />
      </div>
    </>
  );
};

export default AllWarehouses;